import { Injectable } from '@angular/core';

import { GroupItemType } from '@cds/betting-offer';
import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { Column, GridModel } from '@frontend/sports/grid/core/feature/model';
import { NOT_APPLICABLE, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { ModularConfigAccessorService } from '@frontend/sports/widget/core/feature';
import { WidgetTabBarItem } from '@frontend/sports/widget/feature/tab-bar';

import { TabbedChevronClickTrackingData } from '../tabbed-grid/tabbed-grid.model';
import { FavouritesGridNavigation } from './favourites-grid.model';

export const FAVOURITES_GRID_WIDGET_TRACKING_CONSTS = {
    FEATURE_NAME: 'favouritesgridwidget',
    ACTION_EVENT: 'Click',
    EVENT_DETAIL: 'global link',
    FALL_BACK_SPORT: 'sports/0',
    CATEGORY_EVENT: 'Sports Favourites',
    POSITION_EVENT: 'Your Favourites',
} as const;

@Injectable()
export class FavouritesGridTrackingService {
    private active?: WidgetTabBarItem;

    constructor(
        private trackingService: TrackingService,
        private accessorService: ModularConfigAccessorService,
    ) {}

    getTrackingValueForMatchList(item: WidgetTabBarItem): string {
        this.active = item;
        return `sport/${item.itemId}`;
    }

    trackWidgetVisible(grid: GridModel, order: number) {
        const trackingObject: Record<string, any> = {
            ...this.getPartialTrackingObject(grid.sport.id.toString()),
            [trackingConstants.COMPONENT_POSITION_EVENT]: FAVOURITES_GRID_WIDGET_TRACKING_CONSTS.POSITION_EVENT,
            [trackingConstants.COMPONENT_ACTION_EVENT]: trackingConstants.LOAD,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: FAVOURITES_GRID_WIDGET_TRACKING_CONSTS.FEATURE_NAME,
            [trackingConstants.COMPONENT_CONTENT_POSITION]: order.toString(),
            [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
            [trackingConstants.MARKET_ID]: this.getActiveMarketIds(grid.columns),
        };
        this.trackingService.track(trackingConstants.EVENT_CONTENT_VIEW, trackingObject);
    }

    trackTabSelected(item: FavouritesGridNavigation): void {
        this.active = item;

        const trackingObject: Record<string, any> = {
            ...this.getPartialTrackingObject(item.itemId.toString()),
            [trackingConstants.COMPONENT_EVENT_DETAILS]: item.title,
            [trackingConstants.COMPONENT_POSITION_EVENT]: GroupItemType.Sport.toString(),
            [trackingConstants.COMPONENT_CONTENT_POSITION]: item.index,
        };
        this.trackingService.track(trackingConstants.EVENT_TRACKING, trackingObject);
    }

    trackLinkClick(details: TabbedChevronClickTrackingData): void {
        const trackingObject = {
            ...this.getPartialTrackingObject(),
            [trackingConstants.COMPONENT_EVENT_DETAILS]: details.eventDetails,
            [trackingConstants.COMPONENT_URL_CLICKED]: details.urlClicked,
            [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
        };
        this.trackingService.track(trackingConstants.EVENT_TRACKING, trackingObject);
    }

    trackEventClick(event: EventModel, index: number): void {
        this.trackingService.track(trackingConstants.EVENT_TRACKING, {
            ...this.getPartialTrackingObject(event.sport.id.toString()),
            [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_CONTENT_POSITION]: index.toString(),
            [trackingConstants.COMPONENT_EVENT_DETAILS]: FAVOURITES_GRID_WIDGET_TRACKING_CONSTS.FEATURE_NAME,
            [trackingConstants.SPORT_EVENT_ID]: event.id,
        });
    }

    private getTrackingLocationByType(): string {
        if (this.active) {
            return this.getTrackingLocationEvent(GroupItemType.Sport, this.active.itemId);
        }

        return NOT_APPLICABLE;
    }

    private getTrackingLocationEvent(type: string, id: string | number): string {
        return [this.accessorService.getPage(), this.accessorService.getWidget()?.templateName, type, id].filter(Boolean).join('/').toLowerCase();
    }

    private getPartialTrackingObject(sportId?: string): { [key: string]: string } {
        return {
            ...this.accessorService.getWidget()?.trackingData,
            [trackingConstants.SPORT_ID]: sportId ?? NOT_APPLICABLE,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getTrackingLocationByType(),
            [trackingConstants.COMPONENT_LABEL_EVENT]: FAVOURITES_GRID_WIDGET_TRACKING_CONSTS.FEATURE_NAME,
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: FAVOURITES_GRID_WIDGET_TRACKING_CONSTS.CATEGORY_EVENT,
            [trackingConstants.COMPONENT_ACTION_EVENT]: FAVOURITES_GRID_WIDGET_TRACKING_CONSTS.ACTION_EVENT,
        };
    }

    private getActiveMarketIds(columns: Column[]) {
        const activeMarketIds = [];

        for (const column of columns) {
            const activeGroup = column.groups.find((group) => group.active);
            if (activeGroup && activeGroup.id) {
                activeMarketIds.push(activeGroup.id);
            }
        }

        return activeMarketIds.join(',');
    }
}
